// usePrivacyRights.ts — Data Rights admin operations.
//
// Encapsulates three dominus_admin canister calls + the Stripe email lookup
// Worker route. Returns typed wrappers matching the existing hook pattern in
// this directory (useAutoTopUp.ts / useBannedWords.ts).
//
// Worker auth via canister-issued session token (useAdminSession). The shared-secret
// ADMIN_API_TOKEN path is retained on the Worker side for non-interactive callers
// (cron, scripts).

import { useCallback, useState } from 'react';
import type { Identity } from '@dfinity/agent';
import type { Principal } from '@dfinity/principal';
import { createAdminAgent, createAdminActor, type UserDataBundle } from '../lib/adminAgent';
import { useAdminSessionContext } from './useAdminSession';

// ---------------------------------------------------------------------------
// Privacy action type — 7 strings whitelisted by canisters/admin/main.mo.
// Matches the Motoko privacyActionWhitelist exactly.
// ---------------------------------------------------------------------------

export type PrivacyActionType =
  | 'admin_data_access_request_received'
  | 'admin_data_access_fulfilled'
  | 'admin_data_deletion_request_received'
  | 'admin_data_deletion_fulfilled'
  | 'admin_complaint_received'
  | 'admin_complaint_resolved'
  | 'admin_security_disclosure_received';

export const PRIVACY_ACTION_LABELS: Record<PrivacyActionType, string> = {
  admin_data_access_request_received: 'Data access request received',
  admin_data_access_fulfilled: 'Data access request fulfilled',
  admin_data_deletion_request_received: 'Data deletion request received',
  admin_data_deletion_fulfilled: 'Data deletion request fulfilled',
  admin_complaint_received: 'Complaint received',
  admin_complaint_resolved: 'Complaint resolved',
  admin_security_disclosure_received: 'Security disclosure received',
};

// ---------------------------------------------------------------------------
// Stripe lookup result shape — returned by Cash's Worker route
// ---------------------------------------------------------------------------

export interface StripeCustomerMatch {
  stripeCustomerId: string;
  email: string;
  created: number; // Unix seconds
}

// ---------------------------------------------------------------------------
// Hook return type
// ---------------------------------------------------------------------------

export interface UsePrivacyRightsReturn {
  // Per-operation loading/error — independent so UI can show inline state.
  isLookingUpStripe: boolean;
  stripeError: string | null;
  isResolvingPrincipal: boolean;
  resolveError: string | null;
  isExporting: boolean;
  exportError: string | null;
  isLoggingEvent: boolean;
  logError: string | null;

  lookupStripeByEmail: (email: string) => Promise<StripeCustomerMatch[]>;
  lookupPrincipalByStripe: (stripeCustomerId: string) => Promise<Principal | null>;
  exportUserData: (principal: Principal) => Promise<UserDataBundle | null>;
  logPrivacyEvent: (
    actionType: PrivacyActionType,
    targetPrincipalText: string | null,
    details: string,
  ) => Promise<boolean>;
}

function getWorkerBaseUrl(): string {
  // Reuses the same env var checked by useAnalytics.ts — the Worker base URL
  // for all admin Worker routes is consistent across the admin console.
  const base = import.meta.env.VITE_WEBHOOK_BASE_URL as string | undefined;
  return (base && base.trim()) ? base.trim() : 'https://webhook.playdominus.com';
}

// ---------------------------------------------------------------------------
// Hook implementation
// ---------------------------------------------------------------------------

export function usePrivacyRights(identity: Identity | null): UsePrivacyRightsReturn {
  const session = useAdminSessionContext();

  const [isLookingUpStripe, setIsLookingUpStripe] = useState(false);
  const [stripeError, setStripeError] = useState<string | null>(null);

  const [isResolvingPrincipal, setIsResolvingPrincipal] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);

  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const [isLoggingEvent, setIsLoggingEvent] = useState(false);
  const [logError, setLogError] = useState<string | null>(null);

  // -------------------------------------------------------------------------
  // lookupStripeByEmail — Worker route GET /admin/lookup-stripe-by-email
  // Uses canister-issued session token from useAdminSessionContext().
  // -------------------------------------------------------------------------

  const lookupStripeByEmail = useCallback(async (email: string): Promise<StripeCustomerMatch[]> => {
    setIsLookingUpStripe(true);
    setStripeError(null);

    try {
      if (!session.isReady || !session.token) {
        // Attempt one refresh before failing — handles the edge case where the
        // token expired mid-session between the last auto-refresh and this call.
        await session.refresh();
        if (!session.isReady || !session.token) {
          throw new Error('Admin session unavailable. Try refreshing the page.');
        }
      }

      const base = getWorkerBaseUrl();
      const url = `${base}/admin/lookup-stripe-by-email?email=${encodeURIComponent(email.trim())}`;

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${session.token}` },
      });

      if (response.status === 401) {
        // Token was rejected — force a refresh so the next call gets a fresh one.
        await session.refresh();
        throw new Error('Unauthorised. The session token was rejected. Try again.');
      }

      if (!response.ok) {
        throw new Error(`Stripe lookup failed with status ${response.status}.`);
      }

      const json = await response.json() as { matches: StripeCustomerMatch[] };
      return json.matches ?? [];
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Stripe lookup failed. Check the email and try again.';
      setStripeError(msg);
      console.error('[usePrivacyRights] lookupStripeByEmail error:', err);
      return [];
    } finally {
      setIsLookingUpStripe(false);
    }
  }, [session]);

  // -------------------------------------------------------------------------
  // lookupPrincipalByStripe — canister call
  // -------------------------------------------------------------------------

  const lookupPrincipalByStripe = useCallback(async (stripeCustomerId: string): Promise<Principal | null> => {
    if (!identity) return null;

    setIsResolvingPrincipal(true);
    setResolveError(null);

    try {
      const agent = await createAdminAgent(identity);
      const actor = createAdminActor(agent);
      const result = await actor.admin_lookup_principal_by_stripe_customer(stripeCustomerId.trim());

      if ('err' in result) {
        throw new Error(result.err);
      }

      // result.ok is [] | [Principal] — Candid opt. Access index-0 only after
      // length guard; reassign to const to satisfy strict-mode narrowing.
      const optPrincipal = result.ok;
      if (optPrincipal.length > 0) {
        const p = optPrincipal[0];
        return p !== undefined ? p : null;
      }
      return null;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Principal lookup failed.';
      setResolveError(msg);
      console.error('[usePrivacyRights] lookupPrincipalByStripe error:', err);
      return null;
    } finally {
      setIsResolvingPrincipal(false);
    }
  }, [identity]);

  // -------------------------------------------------------------------------
  // exportUserData — canister call
  // -------------------------------------------------------------------------

  const exportUserData = useCallback(async (principal: Principal): Promise<UserDataBundle | null> => {
    if (!identity) return null;

    setIsExporting(true);
    setExportError(null);

    try {
      const agent = await createAdminAgent(identity);
      const actor = createAdminActor(agent);
      const result = await actor.admin_export_user_data(principal);

      if ('err' in result) {
        throw new Error(result.err);
      }

      return result.ok;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Export failed. The canister may be unreachable.';
      setExportError(msg);
      console.error('[usePrivacyRights] exportUserData error:', err);
      return null;
    } finally {
      setIsExporting(false);
    }
  }, [identity]);

  // -------------------------------------------------------------------------
  // logPrivacyEvent — canister call
  // -------------------------------------------------------------------------

  const logPrivacyEvent = useCallback(async (
    actionType: PrivacyActionType,
    targetPrincipalText: string | null,
    details: string,
  ): Promise<boolean> => {
    if (!identity) return false;

    setIsLoggingEvent(true);
    setLogError(null);

    try {
      const agent = await createAdminAgent(identity);
      const actor = createAdminActor(agent);

      // Candid opt text: [] = none, [string] = some(string)
      const optTarget: [] | [string] = targetPrincipalText && targetPrincipalText.trim()
        ? [targetPrincipalText.trim()]
        : [];

      const result = await actor.admin_log_privacy_event(actionType, optTarget, details.trim());

      if ('err' in result) {
        throw new Error(result.err);
      }

      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to record privacy event.';
      setLogError(msg);
      console.error('[usePrivacyRights] logPrivacyEvent error:', err);
      return false;
    } finally {
      setIsLoggingEvent(false);
    }
  }, [identity]);

  return {
    isLookingUpStripe,
    stripeError,
    isResolvingPrincipal,
    resolveError,
    isExporting,
    exportError,
    isLoggingEvent,
    logError,
    lookupStripeByEmail,
    lookupPrincipalByStripe,
    exportUserData,
    logPrivacyEvent,
  };
}
